import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'

import { Body2 } from '@ui/Typography'

import { isDepositBeforeKYC, isSumsubNeeded } from '@utils/AccountAccess/accountKYCStatuses'
import { isTickmillEUType, requiresAppTestImprovement } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'

import { isVerificationExpedited } from '@model/AccountDetailedDto'
import { AccountType } from '@model/AccountType'
import { NameDto } from '@model/NameDto'

import { StepCircle } from '@global/StepCircle/StepCircle'

import { useScrollToTop } from '@hooks/useScrollToTop'

import styles from './SignupStep.module.scss'

export enum OnboardingFlowTypeEnum {
  Standard = 1,
  DepositBeforeKYC,
}

export enum SignupSections {
  AccountType = 1,
  PersonalDetails,
  CompanyDetails,
  FinancialInfo,
  FirstTimeDeposit,
  Documents,
  Sumsub,
  ReviewConfirm,
  EconomicProfile,
}

const TitlePerSection = {
  [SignupSections.AccountType]: 'Sign up.Account Type',
  [SignupSections.PersonalDetails]: 'Sign up.Personal Details',
  [SignupSections.CompanyDetails]: 'Sign up.Company Details',
  [SignupSections.FinancialInfo]: 'Sign up.Financial Info & Experience',
  [SignupSections.FirstTimeDeposit]: 'Fund your Wallet',
  [SignupSections.Documents]: 'Sign up.Documents',
  [SignupSections.Sumsub]: 'Verification',
  [SignupSections.ReviewConfirm]: 'Sign up.Review & Confirm',
  [SignupSections.EconomicProfile]: 'Economic Profile',
  ConfirmRegistration: 'Sign up.Confirm Registration',
  AppropriatenessTest: 'Sign up.Appropriateness Test',
}

interface SignupStepProps {
  section: number
  label?: string
  accountTypeId?: number
  products?: NameDto[]
  subStep?: {
    current: number
    total: number
  }
  isFailed?: boolean
  isCompleted?: boolean
  isHidden?: boolean
}

export const SignupStep: React.FC<SignupStepProps> = (props) => {
  const { section, subStep, isCompleted, isFailed, isHidden, label } = props
  const { account } = useAccountReadContext()
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const tickmillEntity = useSessionEntity()
  useScrollToTop({ effect: [section] })

  const getSignupSteps = () => {
    const steps = [
      {
        section: SignupSections.AccountType,
        title: t(TitlePerSection[SignupSections.AccountType]),
        flows: [AccountType.Individual, AccountType.Corporate],
      },
      {
        section: SignupSections.PersonalDetails,
        title: t(TitlePerSection[SignupSections.PersonalDetails]),
        flows: [AccountType.Individual, AccountType.Corporate],
      },
      {
        section: SignupSections.CompanyDetails,
        title: t(TitlePerSection[SignupSections.CompanyDetails]),
        flows: [AccountType.Corporate],
        hidden: section === SignupSections.AccountType,
      },
      {
        section: SignupSections.FinancialInfo,
        title: requiresAppTestImprovement(tickmillEntity)
          ? t(TitlePerSection.AppropriatenessTest)
          : t(TitlePerSection[SignupSections.FinancialInfo]),
        flows: [AccountType.Individual, AccountType.Corporate],
      },
      {
        section: SignupSections.EconomicProfile,
        title: t(TitlePerSection[SignupSections.EconomicProfile]),
        flows: [AccountType.Individual, AccountType.Corporate],
        hidden: !isTickmillEUType(tickmillEntity),
      },
      {
        section: SignupSections.FirstTimeDeposit,
        title: t(TitlePerSection[SignupSections.FirstTimeDeposit]),
        flows: [AccountType.Individual],
        hidden: !isDepositBeforeKYC(account),
      },
      {
        section: SignupSections.Documents,
        title: t(TitlePerSection[SignupSections.Documents]),
        flows: [AccountType.Individual, AccountType.Corporate],
        hidden: isVerificationExpedited(account) || isSumsubNeeded(account),
      },
      {
        section: SignupSections.Sumsub,
        title: t(TitlePerSection[SignupSections.Sumsub]),
        flows: [AccountType.Individual],
        hidden: isVerificationExpedited(account) || !isSumsubNeeded(account),
      },
      {
        section: SignupSections.ReviewConfirm,
        title: t(TitlePerSection[SignupSections.ReviewConfirm]),
        flows: [AccountType.Individual, AccountType.Corporate],
      },
    ]

    const filteredSteps = steps.filter((step) => {
      if (step.flows.includes(props.accountTypeId || 1)) {
        if (step.hidden !== undefined) {
          return !step.hidden
        } else {
          return true
        }
      } else {
        return false
      }
    })

    const stepTitles = filteredSteps.map((step) => step.title)

    const currentSectionIndex = filteredSteps.findIndex((step) => step.section === section)
    const isLastStep = currentSectionIndex === stepTitles.length - 1

    return (
      <>
        {Array.from({ length: stepTitles.length }, (_, i) => (
          <Body2
            key={i}
            className={classNames(styles.step, {
              [styles.active]: currentSectionIndex === i && !isFailed && !isCompleted,
            })}
          >
            <StepCircle
              isActive={currentSectionIndex === i}
              isCompleted={
                (isCompleted && currentSectionIndex === i) || currentSectionIndex > i || isLastStep
              }
              section={i + 1}
              subStep={subStep}
              isFailed={isFailed && currentSectionIndex === i}
              isHidden={isHidden}
            />
            {!isMobile && <span>{(i === currentSectionIndex && label) || stepTitles[i]}</span>}
          </Body2>
        ))}
      </>
    )
  }

  if (props.accountTypeId === AccountType.Corporate) {
    return <div className={styles.stepsWrapper}>{getSignupSteps()}</div>
  }

  return (
    <section className={styles.stepsBox}>
      <div className={styles.stepsWrapper}>{getSignupSteps()}</div>
    </section>
  )
}
